import { createFileRoute } from "@tanstack/react-router";
import useTranslation from "../../hooks/useTranslation";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  usePaginatedVocabularyList,
  VocabularyDto,
} from "../../hooks/usePaginatedVocabularyList";
import { Toggle } from "../../components/Toggle";
import { TEXT_LANGUAGES } from "../../constants";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useVocabularyUpdate } from "../../hooks/useVocabularyUpdate";
import {
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { normalizeVocabularyTranslationDetails } from "../../utils/vocabularyTranslationDetails";
import { highlightVocabularyWordInSentence } from "../../utils/highlightVocabularyWordInSentence";

export const Route = createFileRoute("/app/vocabulary_trainer")({
  component: VocabularyTrainerPage,
});

type PageState = {
  showWord: boolean;
  showTranslation: boolean;
  showMnemonic: boolean;
  showActions: boolean;
  language: string;
};

function VocabularyTrainerPage() {
  const translation = useTranslation();
  const { languageSpace } = useLanguageSpace("ENGLISH");

  const [pageState, setFieldVisibility] = useLocalStorage<PageState>(
    "voc-trainer-page-v1",
    {
      showWord: true,
      showTranslation: false,
      showMnemonic: false,
      showActions: false,
      language: "ENGLISH",
    },
  );

  const languageLockedToSpace = languageSpace !== "ALL";

  useEffect(() => {
    // Keep trainer language aligned with the active language space unless the user explicitly chooses All.
    if (languageSpace !== "ALL") {
      setFieldVisibility((prev) =>
        prev.language === languageSpace
          ? prev
          : { ...prev, language: languageSpace },
      );
    }
  }, [languageSpace, setFieldVisibility]);

  const languageFilter =
    pageState.language || (languageSpace !== "ALL" ? languageSpace : undefined);

  const paginatedVocabularyList = usePaginatedVocabularyList({
    word_languages:
      languageFilter && languageFilter !== "ALL" ? [languageFilter] : undefined,
  });

  const vocabularyList = paginatedVocabularyList.isSuccess
    ? paginatedVocabularyList.data.pages.flatMap((page) => page.results)
    : [];

  const languageOptions = languageLockedToSpace
    ? TEXT_LANGUAGES.filter((l) => l.id === languageSpace)
    : TEXT_LANGUAGES;

  return (
    <>
      <div className="flex justify-center space-x-1 mt-3 text-center">
        <Toggle
          label={translation.translate("toggle_word")}
          value={pageState.showWord}
          onChange={(v: boolean) => {
            setFieldVisibility({ ...pageState, showWord: v });
          }}
        />
        <Toggle
          label={translation.translate("toggle_translation")}
          value={pageState.showTranslation}
          onChange={(v: boolean) => {
            setFieldVisibility({ ...pageState, showTranslation: v });
          }}
        />
        <Toggle
          label={translation.translate("toggle_mnemonic")}
          value={pageState.showMnemonic}
          onChange={(v: boolean) => {
            setFieldVisibility({ ...pageState, showMnemonic: v });
          }}
        />
        <Toggle
          label={translation.translate("toggle_actions")}
          value={pageState.showActions}
          onChange={(v: boolean) => {
            setFieldVisibility({ ...pageState, showActions: v });
          }}
        />
      </div>
      <select
        className="m-2 p-2 px-3 rounded-xl bg-white text-black border border-gray-300 appearance-auto focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
        disabled={languageLockedToSpace}
        onChange={(e) =>
          setFieldVisibility({ ...pageState, language: e.target.value })
        }
        value={pageState.language}
      >
        <option value="">
          {translation.translate("trainer_language_select")}
        </option>
        {languageOptions.map((elem) => (
          <option key={elem.id} value={elem.id}>
            {elem.pretty}
            {languageLockedToSpace ? " (locked)" : ""}
          </option>
        ))}
      </select>

      {vocabularyList.length > 0 ? (
        <div className="md:grid md:grid-cols-12">
          <div className="md:col-span-6 md:col-start-4">
            <ul className="bg-white p-2">
              {vocabularyList.map((vocabulary) => {
                return (
                  <VocabularyItem
                    key={vocabulary.id}
                    vocabulary={vocabulary}
                    fieldVisibility={pageState}
                  />
                );
              })}
              {paginatedVocabularyList.hasNextPage && (
                <li className="bg-blue-500 p-3 rounded-lg shadow-lg mb-2 w-full">
                  <button
                    className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200"
                    onClick={() => {
                      paginatedVocabularyList.fetchNextPage();
                    }}
                  >
                    Load more
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className="mb-6">
            <BookOpenIcon className="h-16 w-16 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Nothing here yet!
          </h2>
          <p className="text-gray-600 max-w-md">
            Generate a text and tap on unfamiliar words to translate them.
            <br />
            Your translated words will show up here in your vocabulary list.
          </p>
        </div>
      )}
    </>
  );
}

function VocabularyItem(props: {
  vocabulary: VocabularyDto;
  fieldVisibility: PageState;
}) {
  const [toggled, setToggled] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [showMoreGrammar, setShowMoreGrammar] = useLocalStorage<boolean>(
    "vocabulary-more-grammar-toggle-v2",
    false,
  );
  const translation = useTranslation();
  const translationDetails = normalizeVocabularyTranslationDetails({
    translation: props.vocabulary.translation,
    translation_details: props.vocabulary.translation_details,
  });
  const sourceSentence = props.vocabulary.sentence?.trim() || null;
  const translatedSentence = translationDetails.translated_sentence;
  const primarySentence = sourceSentence;
  const secondarySentence = translatedSentence;

  return (
    <li
      className={
        "group text-left bg-slate-100 relative mb-4 p-2 rounded-md " +
        (toggled ? "" : "cursor-pointer")
      }
    >
      <div
        className="bg-slate-200 rounded-md mb-2 p-2"
        onClick={() => setTapped(!tapped)}
      >
        {(tapped || props.fieldVisibility.showWord) && (
          <p className="text-lg font-semibold leading-snug my-0 px-1">
            {props.vocabulary.word}
          </p>
        )}
        {(tapped || props.fieldVisibility.showTranslation) && (
          <div className="px-1 mt-1 space-y-2">
            <p className="text-lg font-medium text-slate-600 leading-snug mt-0">
              {translationDetails.translation_word || props.vocabulary.translation}
            </p>
            <p className="text-left text-sm text-slate-700 leading-snug mt-1">
              {primarySentence
                ? highlightVocabularyWordInSentence(
                    primarySentence,
                    props.vocabulary.word,
                  )
                : translation.translate("vocabulary_source_sentence_unavailable")}
            </p>
            {secondarySentence && (
              <p className="text-left text-sm text-slate-400 leading-snug mt-1">{secondarySentence}</p>
            )}
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {translation.translate("vocabulary_label_grammar")}
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              {translationDetails.grammar.part_of_speech && (
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                  {translation.translate("vocabulary_label_pos")}:{" "}
                  {translationDetails.grammar.part_of_speech}
                </span>
              )}
              {translationDetails.grammar.tense && (
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                  {translation.translate("vocabulary_label_tense")}:{" "}
                  {translationDetails.grammar.tense}
                </span>
              )}
              {translationDetails.grammar.number && (
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                  {translation.translate("vocabulary_label_number")}:{" "}
                  {translationDetails.grammar.number}
                </span>
              )}
              {translationDetails.grammar.gender && (
                <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">
                  {translation.translate("vocabulary_label_gender")}:{" "}
                  {translationDetails.grammar.gender}
                </span>
              )}
            </div>
            {showMoreGrammar && (
              <div className="grid gap-1 text-xs text-slate-600">
                {translationDetails.grammar.mood && (
                  <p>
                    <span className="font-semibold">
                      {translation.translate("vocabulary_label_mood")}:
                    </span>{" "}
                    {translationDetails.grammar.mood}
                  </p>
                )}
                {translationDetails.grammar.person && (
                  <p>
                    <span className="font-semibold">
                      {translation.translate("vocabulary_label_person")}:
                    </span>{" "}
                    {translationDetails.grammar.person}
                  </p>
                )}
                {translationDetails.grammar.case && (
                  <p>
                    <span className="font-semibold">
                      {translation.translate("vocabulary_label_case")}:
                    </span>{" "}
                    {translationDetails.grammar.case}
                  </p>
                )}
                {translationDetails.grammar.syntactic_function && (
                  <p>
                    <span className="font-semibold">
                      {translation.translate(
                        "vocabulary_label_syntactic_function",
                      )}
                      :
                    </span>{" "}
                    {translationDetails.grammar.syntactic_function}
                  </p>
                )}
                {translationDetails.alternative_meanings && (
                  <p>
                    <span className="font-semibold">
                      {translation.translate("vocabulary_label_alternatives")}:
                    </span>{" "}
                    {translationDetails.alternative_meanings}
                  </p>
                )}
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowMoreGrammar((prev) => !prev);
                }}
                className="px-0 py-0 text-xs font-semibold text-slate-600 hover:text-blue-700 focus-visible:outline-none"
              >
                {showMoreGrammar
                  ? translation.translate("vocabulary_less_grammar")
                  : translation.translate("vocabulary_more_grammar")}
              </button>
            </div>
          </div>
        )}
        {toggled ? (
          <MnemonicEditForm vocabulary={props.vocabulary}></MnemonicEditForm>
        ) : (
          (tapped || props.fieldVisibility.showMnemonic) && (
            <p className="text-lg font-medium leading-snug mt-4 px-1">
              {props.vocabulary.mnemonic}
            </p>
          )
        )}
      </div>
      {(tapped || props.fieldVisibility.showActions) && (
        <div className="flex justify-between my-2">
          <button
            className="bg-slate-200 p-2 text-2xl"
            onClick={() => setToggled(!toggled)}
          >
            {toggled ? "❌" : "🖊️"}
          </button>
        </div>
      )}
    </li>
  );
}

type MnemonicFormValues = {
  mnemonic: string;
};

function MnemonicEditForm(props: { vocabulary: VocabularyDto }) {
  const updateVocabulary = useVocabularyUpdate();
  const { register, handleSubmit } = useForm<MnemonicFormValues>();
  const onSubmit: SubmitHandler<MnemonicFormValues> = (data) => {
    void updateVocabulary.mutateAsync({
      id: props.vocabulary.id,
      patchVocabularyDto: {
        mnemonic: data.mnemonic,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex justify-between items-center"
    >
      <input
        {...register("mnemonic")}
        type="text"
        className="grow p-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
        defaultValue={props.vocabulary.mnemonic ?? ""}
      ></input>
      <input
        type="submit"
        value="☑️"
        className="m-2 text-2xl border-gray-300"
      />
    </form>
  );
}
